import React, { useEffect, useState } from 'react';
import DialogBox from '../../common/DialogBox';
import Button from '../../common/Button';
import apiService from '../../../services/ApiService';
import { apiRoutes } from '../../../shared/apiRoutes';

export type ResetDataMode = 'fullWipe' | 'rollOpeningBalances';

export interface ResetTenantDataRequest {
  adminPassword: string;
  mode: ResetDataMode;
  keepUsers: boolean;
  keepParties: boolean;
  keepPartners: boolean;
}

interface ResetTenantDataDialogProps {
  isOpen: boolean;
  tenantId: number | null;
  tenantName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const Trilingual: React.FC<{ en: string; hi: string; gu: string; className?: string }> = ({
  en,
  hi,
  gu,
  className = '',
}) => (
  <div className={`space-y-1.5 ${className}`}>
    <p className="text-sm text-slate-600 leading-relaxed">
      <span className="font-semibold text-slate-500">EN:</span> {en}
    </p>
    <p className="text-sm text-slate-600 leading-relaxed">
      <span className="font-semibold text-slate-500">HI:</span> {hi}
    </p>
    <p className="text-sm text-slate-600 leading-relaxed">
      <span className="font-semibold text-slate-500">GU:</span> {gu}
    </p>
  </div>
);

const extractApiErrorMessage = (error: unknown): string => {
  if (!error) return 'Request failed';
  if (typeof error === 'string') return error;
  const err = error as { message?: string; Message?: string; error?: string };
  return err.message ?? err.Message ?? err.error ?? 'Request failed';
};

const ResetTenantDataDialog: React.FC<ResetTenantDataDialogProps> = ({
  isOpen,
  tenantId,
  tenantName,
  onClose,
  onSuccess,
}) => {
  const [mode, setMode] = useState<ResetDataMode>('rollOpeningBalances');
  const [keepUsers, setKeepUsers] = useState(false);
  const [keepParties, setKeepParties] = useState(false);
  const [keepPartners, setKeepPartners] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMode('rollOpeningBalances');
    setKeepUsers(false);
    setKeepParties(false);
    setKeepPartners(false);
    setConfirmText('');
    setAdminPassword('');
    setAdminPasswordError('');
  }, [isOpen, tenantId]);

  useEffect(() => {
    if (!keepParties) setKeepPartners(false);
  }, [keepParties]);

  const canSubmit =
    Boolean(tenantId) &&
    confirmText.trim().toUpperCase() === 'RESET' &&
    adminPassword.trim().length > 0 &&
    !submitting;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!tenantId || !canSubmit) return;
    const trimmedPassword = adminPassword.trim();
    if (!trimmedPassword) {
      setAdminPasswordError('Admin password is required.');
      return;
    }

    setSubmitting(true);
    setAdminPasswordError('');
    try {
      const body: ResetTenantDataRequest = {
        adminPassword: trimmedPassword,
        mode,
        keepUsers: mode === 'fullWipe' ? keepUsers : true,
        keepParties: mode === 'fullWipe' ? keepParties : true,
        keepPartners: mode === 'fullWipe' ? keepParties && keepPartners : true,
      };
      await apiService.post(apiRoutes.tenants.resetData(tenantId), body);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Tenant data reset completed', type: 'success' },
      }));
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const err = error as { status?: number };
      const msg = extractApiErrorMessage(error);
      if (err.status === 401 || /invalid admin password|incorrect password|wrong password|invalid password/i.test(msg)) {
        setAdminPasswordError('Incorrect password');
        return;
      }
      if (err.status === 403) {
        setAdminPasswordError('You do not have permission to reset tenant data.');
        return;
      }
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: msg || 'Failed to reset tenant data', type: 'error' },
      }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogBox
      isOpen={isOpen}
      onClose={handleClose}
      title="Reset Tenant Data"
      panelClassName="w-[min(100vw-2rem,42rem)] max-h-[90dvh] overflow-y-auto"
    >
      <div className="p-4 space-y-3">
        <p className="text-sm text-slate-800 font-medium">
          {tenantName || 'This tenant'}
        </p>
        <Trilingual
          en="This cannot be undone. Choose one option, type RESET, and enter your Admin password."
          hi="यह वापस नहीं लिया जा सकता। एक विकल्प चुनें, RESET लिखें, और एडमिन पासवर्ड डालें।"
          gu="આ પાછું લઈ શકાતું નથી. એક વિકલ્પ પસંદ કરો, RESET લખો, અને એડમિન પાસવર્ડ નાખો."
        />

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-800 mb-1">Options</legend>

          <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer ${mode === 'rollOpeningBalances' ? 'border-orange-400 bg-orange-50' : 'border-slate-200 bg-white'}`}>
            <input
              type="radio"
              name="reset-mode"
              className="mt-1"
              checked={mode === 'rollOpeningBalances'}
              onChange={() => setMode('rollOpeningBalances')}
              disabled={submitting}
            />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-900">
                Closing balance → Opening balance
              </span>
              <Trilingual
                className="mt-1"
                en="Each party’s current closing balance becomes the new opening balance. All games, sodas, havalas and transactions are deleted. Parties, users and partners are kept. After reset, there is no old game history — only the new opening figures."
                hi="हर पार्टी का आज का क्लोजिंग बैलेंस नया ओपनिंग बैलेंस बन जाएगा। सभी गेम, सौदा, हवाला और ट्रांजैक्शन मिट जाएंगे। पार्टी, यूज़र और पार्टनर रहेंगे। पुराना गेम इतिहास नहीं बचेगा — केवल नया ओपनिंग रहेगा।"
                gu="દરેક પાર્ટીનું હાલનું ક્લોઝિંગ બેલેન્સ નવું ઓપનિંગ બેલેન્સ બનશે. બધી ગેમ, સોદા, હવાલા અને ટ્રાન્ઝેક્શન કાઢી નાખાશે. પાર્ટી, યુઝર અને પાર્ટનર રહેશે. જૂનો ગેમ ઇતિહાસ નહીં રહે — ફક્ત નવું ઓપનિંગ રહેશે."
              />
            </span>
          </label>

          <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer ${mode === 'fullWipe' ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white'}`}>
            <input
              type="radio"
              name="reset-mode"
              className="mt-1"
              checked={mode === 'fullWipe'}
              onChange={() => setMode('fullWipe')}
              disabled={submitting}
            />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-900">
                Reset everything
              </span>
              <Trilingual
                className="mt-1"
                en="Wipe all operational data. Only this tenant record and the Owner login stay. Games, transactions, sodas, havalas, parties and non-Owner users are removed unless you tick the keep options below. Cash party is created again if parties are not kept."
                hi="सारा कामकाजी डेटा मिट जाएगा। केवल यह टेनेंट और ओनर लॉगिन बचेगा। गेम, ट्रांजैक्शन, सोडा, हवाला, पार्टी और ओनर के अलावा यूज़र हटेंगे — जब तक नीचे ‘रखें’ न चुनें। पार्टी न रखने पर कैश पार्टी फिर बनेगी।"
                gu="બધો ઓપરેશનલ ડેટા કાઢી નાખાશે. ફક્ત આ ટેનન્ટ અને ઓનર લોગિન રહેશે. ગેમ, ટ્રાન્ઝેક્શન, soda, હવાલા, પાર્ટી અને ઓનર સિવાયના યુઝર કાઢાશે — સિવાય કે નીચે ‘રાખો’ પસંદ કરો. પાર્ટી ન રાખો તો કેશ પાર્ટી ફરી બનશે."
              />
            </span>
          </label>
        </fieldset>

        {mode === 'fullWipe' ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={keepUsers}
                onChange={(e) => setKeepUsers(e.target.checked)}
                disabled={submitting}
              />
              <span>
                <span className="text-sm font-medium text-slate-800">Keep Users</span>
                <Trilingual
                  className="mt-0.5"
                  en="Do not delete users. Owner is always kept even if this is off."
                  hi="यूज़र न मिटाएँ। यह बंद हो तो भी ओनर रहेगा।"
                  gu="યુઝર ન કાઢો. આ બંધ હોય તો પણ ઓનર રહેશે."
                />
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={keepParties}
                onChange={(e) => setKeepParties(e.target.checked)}
                disabled={submitting}
              />
              <span>
                <span className="text-sm font-medium text-slate-800">Keep Parties</span>
                <Trilingual
                  className="mt-0.5"
                  en="Keep party master records. Balances go back to stored opening (no transactions left)."
                  hi="पार्टी मास्टर रखें। ट्रांजैक्शन नहीं रहेंगे, बैलेंस पुराने ओपनिंग पर आ जाएगा।"
                  gu="પાર્ટી માસ્ટર રાખો. ટ્રાન્ઝેક્શન નહીં રહે, બેલેન્સ જૂના ઓપનિંગ પર આવશે."
                />
              </span>
            </label>
            {keepParties ? (
              <fieldset className="ml-6 space-y-2">
                <legend className="text-xs font-semibold text-slate-700">Partners</legend>
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="reset-partners"
                    className="mt-1"
                    checked={!keepPartners}
                    onChange={() => setKeepPartners(false)}
                    disabled={submitting}
                  />
                  <span>
                    <span className="text-sm font-medium text-slate-800">Clear Partners</span>
                    <Trilingual
                      className="mt-0.5"
                      en="Delete all partner links. Parties themselves stay."
                      hi="सभी पार्टनर लिंक मिटाएँ। पार्टी रहेंगी।"
                      gu="બધી પાર્ટનર લિંક કાઢો. પાર્ટી રહેશે."
                    />
                  </span>
                </label>
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="reset-partners"
                    className="mt-1"
                    checked={keepPartners}
                    onChange={() => setKeepPartners(true)}
                    disabled={submitting}
                  />
                  <span>
                    <span className="text-sm font-medium text-slate-800">Keep Partners</span>
                    <Trilingual
                      className="mt-0.5"
                      en="Keep partner commission links between parties."
                      hi="पार्टियों के बीच पार्टनर/कमीशन लिंक रखें।"
                      gu="પાર્ટીઓ વચ્ચે પાર્ટનર/કમિશન લિંક રાખો."
                    />
                  </span>
                </label>
              </fieldset>
            ) : null}
          </div>
        ) : null}

        <div>
          <label htmlFor="tenant-reset-confirm" className="block text-sm font-medium text-slate-700 mb-1">
            Type RESET to confirm
          </label>
          <input
            id="tenant-reset-confirm"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={submitting}
            autoComplete="off"
            className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="RESET"
          />
        </div>

        <div>
          <label htmlFor="tenant-reset-admin-password" className="block text-sm font-medium text-slate-700 mb-1">
            Admin password
          </label>
          <input
            id="tenant-reset-admin-password"
            type="password"
            value={adminPassword}
            onChange={(e) => {
              setAdminPassword(e.target.value);
              setAdminPasswordError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSubmit();
              }
            }}
            autoComplete="current-password"
            disabled={submitting}
            className="w-full h-[34px] px-2 py-1 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Your admin login password"
          />
          {adminPasswordError ? (
            <p className="text-xs text-red-600 mt-1">{adminPasswordError}</p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            isProcessing={submitting}
            className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-md"
          >
            Reset Data
          </Button>
        </div>
      </div>
    </DialogBox>
  );
};

export default ResetTenantDataDialog;
